import { FormikProvider, useFormik } from 'formik'

import { Button, Dialog, Form, Text } from '@/ui'
import { getFieldNames, makeReqStringSchema } from '@/base'
import { TypographyStyles } from '@/styles'
import { AddUserIncomePlanSourceToForm } from '@/features/income-planning/incomePlanning.slice'
import { PayerTypeSelect } from '@/features/income-planning/plan-flows/PayerTypeSelect'

export const AddIncomeSourceDialog = ({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void
  onSubmit: (income: AddUserIncomePlanSourceToForm) => void
}) => {
  const formik = useFormik({
    initialValues: {
      name: '',
      source: '' as '' | 'therapy' | 'non_therapy',
      cadence: '' as '' | 'hourly' | 'monthly',
      payerType: '',
    },
    onSubmit: ({ name, source, cadence, payerType }) => {
      if (!source) {
        return
      }

      onSubmit({
        name,
        source,
        cadence: cadence || 'hourly',
        payerType,
        rate: source === 'therapy' ? 75 : cadence === 'hourly' ? 50 : 1000,
        numberPerMonth:
          source === 'therapy' || cadence === 'hourly' ? 20 : undefined,
      })
    },
  })

  const { submitForm, values } = formik

  const fieldNames = getFieldNames(formik)

  return (
    <Dialog open onClose={onCancel}>
      <Dialog.Content>
        <FormikProvider value={formik}>
          <Dialog.Title>Add income</Dialog.Title>

          <Text variant={TypographyStyles.BODY_SM} className="mt-6">
            Adding hourly income enables you to track income that you get paid
            at an hourly rate. You can track hours worked per week, per month,
            or per year.
          </Text>

          <Form.Input
            name={fieldNames.name}
            label="Income source name"
            placeholder="Enter text"
            schema={makeReqStringSchema()}
            wrapperClassName="mt-4"
          />

          <div className="mt-4">
            <Form.Field
              name={fieldNames.source}
              label="Income type"
              schema={makeReqStringSchema()}
            >
              <div className="flex gap-2">
                <Form.Toggle
                  value="therapy"
                  name={fieldNames.source}
                  variant="outline"
                >
                  Therapy Income
                </Form.Toggle>

                <Form.Toggle
                  value="non_therapy"
                  name={fieldNames.source}
                  variant="outline"
                >
                  Non-Therapy Income
                </Form.Toggle>
              </div>
            </Form.Field>
          </div>

          {values.source === 'therapy' && (
            <div className="mt-4">
              <PayerTypeSelect
                label="Payer type (Optional)"
                name={fieldNames.payerType}
                placeholder="Payer types not included in the selections above"
              />
            </div>
          )}

          {values.source === 'non_therapy' && (
            <div className="mt-4">
              <Form.Select
                name={fieldNames.cadence}
                label="Period"
                options={[
                  { text: 'Per hour', value: 'hourly' },
                  { text: 'Per month', value: 'monthly' },
                ]}
                placeholder="Select time period"
                schema={makeReqStringSchema()}
              />
            </div>
          )}

          <Dialog.Footer className="mt-6">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={submitForm}>Save</Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
